<!-- eslint-disable no-console -->
<script lang="ts" setup>
import type * as z from 'zod'
import type { InstallationErrorStatus } from './composables/useInstallationAlerts'
import { SolAlert, SolButton, SolCheckbox, SolDivider, SolInputText, SolInputTextPassword, SolModal } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { useRoute } from 'vue-router'
import OpenInNew from '~icons/material-symbols/open-in-new'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useAccessUserCredentials from '~/store-v2/installation/useAccessUserCredentials'
import useChangeCredentials from '~/store-v2/installation/useChangeCredentials'
import useRegisterPartnerCredentialsCheck from '~/store-v2/installation/useRegisterPartnerCredentialsCheck'
import useInstallationAlerts, { INSTALLATION_ERROR_STATUSES_NEEDING_ACCESS_USER } from './composables/useInstallationAlerts'
import getSchema, { brandSchema, getAvaliableFieldByBrand } from './schemas/inversorCredentials.schema'
import { getMigrationVideoByBrand, getPortalNameByBrand, getPortalUrlByBrand } from './schemas/portals'
import { useInstallationCommissioningFlowInject } from './useInstallationCommissioningFlow'
import useInstallationFlow, { steps } from './useInstallationFlow'

// COMPOSABLES & SETUP

const { t } = useI18n()
const route = useRoute<any>()

const {
  getErrorAlertConfig,
  showInstallationErrorToast,
} = useInstallationAlerts()

// REACTIVE STATE

const projectId = computed(() => route.params.id as string)
const inversorBrand = ref('DEFAULT')
const CREDENTIALS_MOCK = ref(false)

// Loading states
const shouldFetchAccessCredentials = ref(false)
const hasProcessedCredentialMismatch = ref(false)
const isComponentMounted = ref(false)
const isProcessingCredentialMismatch = ref(false)
const isAutoFillingLogin = ref(false)
const isLoginAutoFilled = ref(false)

// Modal states
const showMigrationVideoModal = ref(false)

// Alert states
const alertType = ref<'informative' | 'error'>('informative')
const alertTitle = ref('Apenas dados de acesso de administrador')
const alertText = ref<string | string[]>('a planta com os números de série dos inversores deve ter sido criada com o usuário acima no portal de monitoramento.')
const keepCredentialMismatchAlert = ref(false)
const isInCredentialMismatchFlow = ref(false)

const authorizationError = ref('')

// COMPOSABLES

const commissioningFlow = useInstallationCommissioningFlowInject()

const {
  data: accessUserCredentials,
  isError: isAccessUserCredentialsError,
  isFetched: isAccessUserCredentialsFetched,
} = useAccessUserCredentials(inversorBrand, shouldFetchAccessCredentials)
const { handleStepChange, setLastError, stepState, clearStepContext } = useInstallationFlow(projectId)
const { data: isSofarAlertEnabled } = useFeatureFlag(FEATURE_FLAGS.enable_sofar_installation_alert, false)

// Computed values
const cameFromCredentialMismatch = computed(() => stepState.value?.context === 'CREDENTIAL_MISMATCH')
const portalName = computed(() => getPortalNameByBrand(inversorBrand.value))
const portalUrl = computed(() => getPortalUrlByBrand(inversorBrand.value))

const showCredentialMismatchLoading = computed(() => isProcessingCredentialMismatch.value)

const shouldShowInverterAlert = computed(() => {
  if (inversorBrand.value === 'DEYE') {
    return true
  }
  if (inversorBrand.value === 'SOFAR') {
    return isSofarAlertEnabled.value === true
  }
  return false
})

const migrationVideoUrl = computed(() => {
  const videoUrl = getMigrationVideoByBrand(inversorBrand.value)
  return videoUrl ? videoUrl.replace('watch?v=', 'embed/') : ''
})

// FORM SETUP

const schema = getSchema()
const form = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: {
    brand: 'DEFAULT',
  },
})

// Form fields
const [brandModel] = form.defineField('brand')
const [loginModel, loginProps] = form.defineField('login')
const [passwordModel, passwordProps] = form.defineField('password')
const authorizationModel = ref(false)

// Computed form fields
const fields = computed(() => getAvaliableFieldByBrand(brandModel.value as any, schema))

// MUTATIONS

const {
  mutateAsync: registerPartnerCredentialsCheck,
  isPending: isPendingCredentialsCheck,
  reset: resetCredentialsCheck,
} = useRegisterPartnerCredentialsCheck(inversorBrand)

const {
  mutateAsync: changeCredentials,
  isPending: isPendingChangeCredentials,
  reset: resetChangeCredentials,
} = useChangeCredentials(inversorBrand.value)

const isPendingCredentials = computed(() => {
  if (isInCredentialMismatchFlow.value) {
    return isPendingChangeCredentials.value
  }
  return isPendingCredentialsCheck.value
})

// UTILITY FUNCTIONS

function isValidBrand(value: string): value is z.infer<typeof brandSchema> {
  return (brandSchema.options as readonly string[]).includes(value)
}

function openPortalInNewTab() {
  window.open(portalUrl.value, '_blank', 'noopener,noreferrer')
}

function openMigrationVideo() {
  const videoUrl = getMigrationVideoByBrand(inversorBrand.value)

  if (videoUrl) {
    showMigrationVideoModal.value = true
  }
}

function previousStep() {
  handleStepChange(steps.LABELS)
}

function resetAlert() {
  alertType.value = 'informative'
  alertTitle.value = 'Apenas dados de acesso de administrador'
  alertText.value = 'a planta com os números de série dos inversores deve ter sido criada com o usuário acima no portal de monitoramento.'
  keepCredentialMismatchAlert.value = false
  isInCredentialMismatchFlow.value = false
}

function setCredentialMismatchAlert() {
  keepCredentialMismatchAlert.value = true
  applyErrorConfig('CREDENTIAL_MISMATCH', 'alert')
}

function showEndCustomerCredentialError() {
  alertType.value = 'error'
  alertTitle.value = 'Você inseriu dados de acesso de cliente final'
  alertText.value = 'Insira os dados de acesso de administrador da empresa e tente novamente.'
}

// FORM MANAGEMENT

// ERROR HANDLING

function applyErrorConfig(
  status: InstallationErrorStatus,
  target: 'alert' | 'lastError',
  accessUser?: string,
): void {
  const needsAccessUser = INSTALLATION_ERROR_STATUSES_NEEDING_ACCESS_USER.includes(status)
  const alertConfig = getErrorAlertConfig(status, {
    platformName: portalName.value,
    ...(needsAccessUser && accessUser && { accessUser }),
  })

  if (alertConfig) {
    if (target === 'alert') {
      alertType.value = 'error'
      alertTitle.value = alertConfig.title
      alertText.value = alertConfig.text
    }
    else {
      setLastError({
        title: alertConfig.title,
        text: alertConfig.text,
      })
    }
  }
}

// CREDENTIALS VALIDATION

async function validateCredentials(values: any): Promise<{ success: boolean, message?: string }> {
  try {
    let response: any

    if (isInCredentialMismatchFlow.value) {
      response = await changeCredentials({ brandParam: inversorBrand.value, passwordParam: passwordModel.value || '' })
    }
    else {
      response = await registerPartnerCredentialsCheck({
        username: 'login' in values ? values.login : '',
        password: 'password' in values ? values.password : '',
      })
    }

    return response
  }
  catch (error) {
    console.error('Credentials validation error:', error)

    if (isInCredentialMismatchFlow.value && error && typeof error === 'object' && 'message' in error) {
      return { success: false, message: (error as any).message }
    }

    return { success: false, message: 'TECHNICAL_FAILURE' }
  }
}

function handleCredentialsValidationError(message?: string): void {
  if (message === 'END_CUSTOMER_CREDENTIAL') {
    showEndCustomerCredentialError()
  }
  else {
    const status = (message || 'TECHNICAL_FAILURE') as InstallationErrorStatus
    applyErrorConfig(status, 'alert')
  }
}

// MAIN SUBMIT HANDLER

const handleSubmit = form.handleSubmit(async (values) => {
  if (!authorizationModel.value) {
    authorizationError.value = 'Para continuar, por favor, marque a opção acima.'
    return
  }
  authorizationError.value = ''

  if (!isInCredentialMismatchFlow.value) {
    resetAlert()
  }

  const validationResult = await validateCredentials(values)

  if (validationResult.success) {
    commissioningFlow?.holdUntilStatusChanges('COMMISSIONING_FAILED')
    handleStepChange(steps.POLLING)
    if (isInCredentialMismatchFlow.value) {
      isInCredentialMismatchFlow.value = false
    }
  }
  else {
    if (isInCredentialMismatchFlow.value) {
      if (validationResult.message === 'CREDENTIAL_MISMATCH') {
        showInstallationErrorToast('CREDENTIAL_MISMATCH', { platformName: portalName.value })
      }
      else {
        showInstallationErrorToast('TECHNICAL_FAILURE', { platformName: portalName.value })
      }
    }
    else {
      handleCredentialsValidationError(validationResult.message)
    }
  }

  if (isInCredentialMismatchFlow.value) {
    resetChangeCredentials()
  }
  else {
    resetCredentialsCheck()
  }
})

// WATCHERS & LIFECYCLE

watchEffect(() => {
  if (CREDENTIALS_MOCK.value) {
    handleStepChange(steps.ANALYSIS)
  }
})

watch(cameFromCredentialMismatch, (isFromMismatch) => {
  if (isFromMismatch) {
    isProcessingCredentialMismatch.value = true
    isInCredentialMismatchFlow.value = true
    setCredentialMismatchAlert()
    hasProcessedCredentialMismatch.value = true
  }
  else if (!hasProcessedCredentialMismatch.value) {
    isProcessingCredentialMismatch.value = false
  }
}, { immediate: true })

watch([cameFromCredentialMismatch, portalName, isComponentMounted], ([isFromMismatch, portal, isMounted]) => {
  if (isMounted && isFromMismatch && portal && portal !== 'DEFAULT' && inversorBrand.value !== 'DEFAULT') {
    shouldFetchAccessCredentials.value = true
  }
  else if (!hasProcessedCredentialMismatch.value && isMounted) {
    shouldFetchAccessCredentials.value = false
  }
}, { immediate: true })

function finishCredentialMismatchBootstrap(accessUser?: string) {
  if (accessUser !== undefined) {
    isAutoFillingLogin.value = true
    loginModel.value = accessUser
    isLoginAutoFilled.value = true
  }

  nextTick(() => {
    clearStepContext()
    hasProcessedCredentialMismatch.value = false
    isProcessingCredentialMismatch.value = false
    isAutoFillingLogin.value = false
  })
}

watch(accessUserCredentials, (newData) => {
  if (!hasProcessedCredentialMismatch.value)
    return

  if (newData) {
    finishCredentialMismatchBootstrap(newData.data?.access_user || '')
  }
})

watch(
  () => ({
    shouldFetch: shouldFetchAccessCredentials.value,
    fetched: isAccessUserCredentialsFetched.value,
    errored: isAccessUserCredentialsError.value,
  }),
  ({ shouldFetch, fetched, errored }) => {
    if (!hasProcessedCredentialMismatch.value || !shouldFetch || !fetched)
      return

    if (errored)
      finishCredentialMismatchBootstrap()
  },
)

watch(authorizationModel, (newValue) => {
  if (newValue) {
    authorizationError.value = ''
  }
})

watch([loginModel, passwordModel], (newValues) => {
  if (alertType.value === 'error' && !isAutoFillingLogin.value && !keepCredentialMismatchAlert.value) {
    resetAlert()
  }

  if (isLoginAutoFilled.value && newValues[0] === '' && !isAutoFillingLogin.value) {
    isLoginAutoFilled.value = false
  }
})

watch(inversorBrand, (newBrand: string) => {
  const upper = newBrand.toUpperCase()
  if (isValidBrand(upper)) {
    form.setFieldValue('brand', upper)
  }
  else {
    form.setFieldValue('brand', 'DEFAULT')
  }
}, { immediate: true })

watch(
  () => commissioningFlow?.commissioningPayload.value?.data?.monitorings,
  (monitorings) => {
    if (!monitorings?.length)
      return

    const failedMonitoring = monitorings.find(m => m.status === 'COMMISSIONING_FAILED')
    const brand = (failedMonitoring ?? monitorings[0])?.brand

    if (brand) {
      inversorBrand.value = brand.toUpperCase()
    }

    isComponentMounted.value = true
  },
  { immediate: true, deep: true },
)

watch(
  () => commissioningFlow?.credentialsAlert.value,
  (status) => {
    if (!status)
      return

    if (status === 'CREDENTIAL_MISMATCH') {
      setCredentialMismatchAlert()
      return
    }

    const accessUser = commissioningFlow?.commissioningPayload.value?.data?.access_user
    applyErrorConfig(status, 'alert', accessUser ?? undefined)
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="showCredentialMismatchLoading" class="px-2xs py-2xs rounded-lg bg-neutral-high-pure w-full">
    <div class="flex flex-col justify-center items-center w-full h-[40vh] gap-2">
      <div class="spinner-container" />

      <span class="text-xs text-neutral-low-light mt-2xs">
        Verificando credenciais de acesso...
      </span>
    </div>
  </div>

  <SolAlert
    v-else-if="alertType === 'error'"
    id="admin-credentials-alert"
    :feedback="alertType"
    :title="alertTitle"
  >
    <div>
      <template v-if="Array.isArray(alertText)">
        <template v-for="(line, index) in alertText" :key="index">
          <span>{{ line }}</span>
          <br v-if="index < alertText.length - 1">
        </template>
      </template>
      <span v-else>{{ alertText }}</span>
    </div>
  </SolAlert>

  <div v-if="!showCredentialMismatchLoading" class="mt-4 px-2xs py-2xs rounded-lg bg-neutral-high-pure w-full">
    <div class="flex flex-1 flex-col">
      <h2 class="font-highlight mb-2 fonts-heading-h3">
        Credenciais do portal de monitoramento
      </h2>

      <SolDivider thickness="x-small" orientation="horizontal" class="mb-2" />

      <p class="text-2xs">
        Para validar sua instalação com inversores

        <a
          :href="portalUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline font-bold"
        >
          {{ portalName }},
        </a>

        por favor, informe as suas credenciais de instalador do portal de monitoramento.
      </p>

      <div>
        <div v-if="shouldShowInverterAlert" class="credentials-box mt-4">
          <div class="flex items-center justify-between w-full">
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-1">
                <h3 class="text-brand-primary-pure text-3xs font-bold">
                  Agora, instalações com inversores {{ inversorBrand }} só podem ser validadas por meio do portal {{ portalName }}.
                </h3>

                <p class="text-brand-primary-pure text-3xs font-regular">
                  Caso ainda não tenha conta, cadastre-se e migre já suas plantas.
                </p>
              </div>

              <div class="flex gap-3">
                <SolButton
                  id="create-account"
                  variant="secondary"
                  size="small"
                  @click="openPortalInNewTab"
                >
                  Crie sua conta
                </SolButton>

                <SolButton
                  v-if="inversorBrand !== 'SOFAR'"
                  id="see-migration"
                  variant="secondary"
                  size="small"
                  @click="openMigrationVideo"
                >
                  Veja como migrar do {{ portalName }}
                </SolButton>
              </div>
            </div>

            <div class="hidden md:site:flex flex-shrink-0">
              <img
                src="/images/installation/portal-connection.svg"
                alt="Conexão com portal de monitoramento"
              >
            </div>
          </div>
        </div>

        <div class="credentials-grid gap-5 mt-4 w-full">
          <div class="flex flex-col justify-between">
            <label class="text-neutral-low-dark text-3xs font-bold">
              Portal de monitoramento
            </label>

            <div>
              <a
                :href="portalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="portal-link"
              >
                <span>{{ portalName }}</span>

                <OpenInNew class="icon-size-small" />
              </a>
            </div>
          </div>

          <SolInputText
            v-if="fields.includes('login')"
            id="login"
            v-model="loginModel"
            name="login"
            v-bind="loginProps"
            :error="form.errors.value.login"
            :label="t('installation.inverter.credentials.login.label')"
            :disabled="isLoginAutoFilled"
            placeholder="Digite o login de administrador da empresa"
            helper-text="A validação só pode ser feita com o seu usuário de instalador."
          />

          <SolInputTextPassword
            v-if="fields.includes('password')"
            id="password"
            v-model="passwordModel"
            name="password"
            v-bind="passwordProps"
            :error="form.errors.value.password"
            :label="t('installation.inverter.credentials.password.label')"
            placeholder="Digite a senha"
          />
        </div>

        <SolAlert
          id="admin-credentials-info"
          feedback="informative"
          class="mt-4"
        >
          <div>
            <span class="font-bold">Evite reprovações: </span>

            <span>a planta com os números de série dos inversores deve ter sido criada com o usuário acima no portal de monitoramento.</span>
          </div>
        </SolAlert>

        <div class="mt-4">
          <SolCheckbox
            id="authorization"
            name="authorization"
            value="authorization"
            @change="authorizationModel = !authorizationModel"
          >
            <span class="text-neutral-low-medium text-3xs">
              Autorizo a Solfácil a utilizar o meu acesso de instalador do Portal {{ portalName }} para acompanhar os projetos e sua operação, conforme a Política de Privacidade.
            </span>
          </SolCheckbox>
          <span v-if="authorizationError" class="error-message">{{ authorizationError }}</span>
        </div>

        <SolDivider thickness="x-small" orientation="horizontal" class="my-4" />

        <div class="flex justify-end gap-3 flex-row lg:flex-col lg:gap-2">
          <div>
            <SolButton
              id="back"
              variant="tertiary"
              size="medium"
              class="w-full"
              icon="arrow-left"
              @click="previousStep"
            >
              Voltar
            </SolButton>
          </div>

          <div>
            <SolButton
              id="next"
              variant="primary"
              size="medium"
              class="w-full"
              :loading="isPendingCredentials || isProcessingCredentialMismatch"
              :disabled="isPendingCredentials || isProcessingCredentialMismatch"
              @click="handleSubmit"
            >
              {{
                isProcessingCredentialMismatch
                  ? 'Verificando credenciais...'
                  : 'Conectar'
              }}
            </SolButton>
          </div>
        </div>
      </div>
    </div>
  </div>

  <SolModal
    id="migration-video-modal"
    v-model:is-open="showMigrationVideoModal"
    :title="`Como migrar a planta para o portal ${portalName}`"
    :size="{ desktop: 'extra-large', mobile: 'full' }"
  >
    <div class="aspect-video w-full min-h-[500px]">
      <iframe
        v-if="migrationVideoUrl"
        :src="migrationVideoUrl"
        class="w-full h-full min-h-[500px] rounded-lg"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      />
    </div>
  </SolModal>
</template>

<style scoped lang="scss">
.spinner-container {
  position: relative;
  background-image: url('/images/installation/spinner-inner.png');
  background-size: 50%;
  background-position: center;
  background-repeat: no-repeat;
  width: 100px;
  height: 100px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 3px;
    left: 0;
    background-image: url('/images/installation/spinner-outer.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    animation: spin 1s linear infinite;
    transform-origin: center;
  }
}

.portal-link {
  @apply inline-flex items-center gap-1;
  @apply text-neutral-low-medium text-2xs;
  @apply underline cursor-pointer py-micro;

  &:hover {
    @apply opacity-semiopaque;
  }
}

.credentials-grid {
  @apply grid grid-cols-1 md:site:grid-cols-3;
}

.credentials-box {
  @apply flex flex-col justify-center gap-4;
  @apply p-4xs rounded-md;
  @apply border border-solid border-neutral-high-medium;
  @apply bg-neutral-high-light;
}

.error-message {
  @apply text-feedback-negative-pure text-3xs mt-micro;
  @apply block;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
